import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Styles from 'src/components/Styles';
import Carousel, {
  ICarouselInstance,
  Pagination,
} from 'react-native-reanimated-carousel';
import {useSharedValue} from 'react-native-reanimated';
import {Dimensions, StyleSheet, View} from 'react-native';
import AccountStore from 'src/stores/AccountStore';
import {IsConnectedProps} from 'src/types/Screen';
import {MoblicoPromotionType} from 'src/types/Promotions';
import FirebaseAnalytic from 'src/nativeModules/FirebaseAnalytic';
import Localized from 'src/constants/AppStrings';
import Settings from 'src/Settings';
import HomeScreenTitles from 'src/components/elements/home/HomeScreenTitles';
import PromotionItem from '../promotions/PromotionCarouselItem';
import {getDescriber} from './descriptors/campusComponents/DescriptorType';

type EngagementCarouselProps = IsConnectedProps & {
  showHeader?: boolean;
  onViewAll?: () => void;
  onToastEnable?: () => void;
};

const width = Dimensions.get('window').width;

const {getEngagementCarouselPaginationActiveDotColor} = getDescriber();

const EngagementCarousel = memo((props: EngagementCarouselProps) => {
  const [promotionsData, setPromotionsData] = useState<MoblicoPromotionType[]>(
    [],
  );
  const carouselRef = useRef<ICarouselInstance>(null);
  const progress = useSharedValue<number>(0);
  const [loadingStates, setLoadingStates] = useState([]);
  const [allImagesLoaded, setAllImagesLoaded] = useState(false);

  useEffect(() => {
    getPromotions();
    FirebaseAnalytic.trackEvent('componentDidMount', 'EngagementCarousel', {
      ...props,
    });
    AccountStore.addChangeListener(getPromotions);
    return () => {
      AccountStore.removeChangeListener(getPromotions);
    };
  }, [props]);

  useEffect(() => {
    if (promotionsData.length > loadingStates.length) {
      setLoadingStates((prev) => [
        ...prev,
        ...Array(promotionsData.length - prev.length).fill(true),
      ]);
    }
  }, [loadingStates.length, promotionsData]);

  useEffect(() => {
    if (
      Settings.isRefiveAnd365Pay() &&
      promotionsData.length > 0 &&
      !allImagesLoaded
    ) {
      const timeoutId = setTimeout(() => {
        props.onToastEnable();
      }, 10000);

      return () => clearTimeout(timeoutId);
    }
  }, [promotionsData.length, allImagesLoaded, props]);

  useEffect(() => {
    if (loadingStates.length > 0 && loadingStates.every((state) => !state)) {
      setAllImagesLoaded(true);
    }
  }, [loadingStates]);

  const getPromotions = () => {
    if (AccountStore.isConsumerEngagementEnabled()) {
      let moblicoPromotions: MoblicoPromotionType[] =
        AccountStore.getMoblicoPromosResponse();
      if (moblicoPromotions.length > 10) {
        moblicoPromotions = moblicoPromotions.slice(0, 11);
      }
      if (Settings.isRefiveAnd365Pay()) {
        moblicoPromotions = moblicoPromotions.slice(0, 5);
      }
      setPromotionsData(moblicoPromotions);
    }
  };

  const handleImageLoad = (index) => {
    setLoadingStates((prev) => {
      const updatedStates = [...prev];
      updatedStates[index] = false;
      return updatedStates;
    });
  };

  const renderItem = ({
    item,
    index,
  }: {
    item: MoblicoPromotionType;
    index: number;
  }) => (
    <PromotionItem
      item={item}
      index={index}
      isLoading={loadingStates[index]}
      onLoad={() => handleImageLoad(index)}
    />
  );

  const onPressPagination = useCallback(
    (index: number) => {
      carouselRef.current?.scrollTo({
        count: index - progress.value,
        animated: true,
      });
    },
    [progress.value],
  );

  const baseOptions = useMemo(() => {
    return {
      vertical: false,
      width,
      height: width * 0.75,
      enabled: promotionsData.length > 1,
      mode: 'parallax',
      modeConfig: {
        parallaxScrollingOffset: 35,
        parallaxScrollingScale: 0.95,
      },
    } as const;
  }, [promotionsData.length]);

  const isMoreThanOnePromotion = useMemo(() => {
    return promotionsData.length > 1;
  }, [promotionsData]);

  return promotionsData.length > 0 ? (
    <>
      {props.showHeader ? (
        <HomeScreenTitles
          label={Localized.Labels.latest_offers}
          isConnected={props.isConnected}
          showViewAll={isMoreThanOnePromotion}
          onViewAll={props.onViewAll}
          style={{paddingTop: Styles.Spacing.m2}}
          testID={'qa_LatestOffers_btn'}
        />
      ) : null}
      <View testID="engagement-carousel" style={[styles.content]}>
        <Carousel
          ref={carouselRef}
          {...baseOptions}
          onProgressChange={progress}
          data={promotionsData}
          renderItem={renderItem}
          loop={false}
          onConfigurePanGesture={(panGesture) =>
            panGesture.activeOffsetX([-10, 10]).activeOffsetY([-500, 500])
          }
          testID="engagement-carousel"
        />
        {isMoreThanOnePromotion ? (
          <Pagination.Basic
            progress={progress}
            data={promotionsData}
            size={10}
            dotStyle={styles.paginationDot}
            activeDotStyle={styles.activePaginationDot}
            containerStyle={styles.paginationContainer}
            onPress={onPressPagination}
          />
        ) : null}
      </View>
    </>
  ) : null;
});

const styles = StyleSheet.create({
  content: {
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: Styles.Spacing.m2,
  },
  paginationDot: {
    borderRadius: 50,
    backgroundColor: Styles.inActiveDotColor,
  },
  activePaginationDot: {
    backgroundColor: getEngagementCarouselPaginationActiveDotColor(),
  },
  paginationContainer: {
    gap: Styles.Spacing.m2,
  },
});

export default EngagementCarousel;
